import { useCallback } from 'react';
import { useI18nContext } from '../contexts/I18nContext';
import { LanguageHookReturn, LanguageCode } from '../types/i18n.types';

/**
 * Hook for language switching and language-related utilities
 * @returns Language management utilities
 */
export const useLanguage = (): LanguageHookReturn => {
  const context = useI18nContext();

  // Utility function to switch to next language in supported list
  const switchToNextLanguage = useCallback(async (): Promise<void> => {
    const currentIndex = context.supportedLanguages.findIndex(
      lang => lang.code === context.currentLanguage
    );
    const nextIndex = (currentIndex + 1) % context.supportedLanguages.length;
    const nextLanguage = context.supportedLanguages[nextIndex];

    if (nextLanguage) {
      await context.changeLanguage(nextLanguage.code);
    }
  }, [context]);

  // Quick switch between Vietnamese and English
  const toggleLanguage = useCallback(async (): Promise<void> => {
    const newLanguage: LanguageCode = context.currentLanguage === 'vi' ? 'en' : 'vi';
    await context.changeLanguage(newLanguage);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- using stable property deps instead of full context object to avoid extra rerenders
  }, [context.currentLanguage, context.changeLanguage]);

  // Get language display name
  const getCurrentLanguageName = useCallback((native = false) => {
    const currentLang = context.supportedLanguages.find(
      lang => lang.code === context.currentLanguage
    );
    return currentLang ? (native ? currentLang.nativeName : currentLang.name) : 'Unknown';
  }, [context.currentLanguage, context.supportedLanguages]);

  // Get language flag emoji
  const getCurrentLanguageFlag = useCallback(() => {
    const currentLang = context.supportedLanguages.find(
      lang => lang.code === context.currentLanguage
    );
    return currentLang?.flag || '🌐';
  }, [context.currentLanguage, context.supportedLanguages]);

  return {
    // Core language state
    currentLanguage: context.currentLanguage,
    changeLanguage: context.changeLanguage,
    isChangingLanguage: context.isChangingLanguage,
    supportedLanguages: context.supportedLanguages,
    isRTL: context.isRTL,

    // Convenience flags
    isVietnamese: context.currentLanguage === 'vi',
    isEnglish: context.currentLanguage === 'en',

    // Additional utilities
    toggleLanguage,
    switchToNextLanguage,
    getCurrentLanguageName,
    getCurrentLanguageFlag
  };
};

export default useLanguage;